import { ChevronDown, Loader2, RefreshCw } from "lucide-react";
import React, { useCallback, useState } from "react";
import { graphql, useMutation } from "react-relay";
import { Button } from "src/components/ui/button";
import { Label } from "src/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "src/components/ui/select";
import { McpOauthAdvancedSettingsFetchMethodsMutation } from "src/pages/SettingsPage/routes/__generated__/McpOauthAdvancedSettingsFetchMethodsMutation.graphql";
import { McpOauthAdvancedSettingsSetOverrideMutation } from "src/pages/SettingsPage/routes/__generated__/McpOauthAdvancedSettingsSetOverrideMutation.graphql";

const AUTH_METHOD_LABELS: Record<string, string> = {
  none: "None (PKCE / Public Client)",
  client_secret_post: "Client Secret (POST body)",
  client_secret_basic: "Client Secret (HTTP Basic)",
};

const fetchMethodsMutation = graphql`
  mutation McpOauthAdvancedSettingsFetchMethodsMutation($input: FetchMcpOauthMethodsInput!) {
    fetchMcpOauthMethods(input: $input) {
      methods
      error
    }
  }
`;

const setOverrideMutation = graphql`
  mutation McpOauthAdvancedSettingsSetOverrideMutation($input: SetMcpOauthMethodOverrideInput!) {
    setMcpOauthMethodOverride(input: $input) {
      server {
        id
      }
      error
    }
  }
`;

interface McpOauthAdvancedSettingsProps {
  url: string;
  selectedMethod: string;
  onMethodChange: (method: string) => void;
  /** Pass the server's GlobalID for existing servers so the override is saved immediately. */
  serverId?: string;
}

export default function McpOauthAdvancedSettings({
  url,
  selectedMethod,
  onMethodChange,
  serverId,
}: McpOauthAdvancedSettingsProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [methods, setMethods] = useState<string[]>([]);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [commitFetch, isFetching] = useMutation<McpOauthAdvancedSettingsFetchMethodsMutation>(fetchMethodsMutation);
  const [commitSetOverride] = useMutation<McpOauthAdvancedSettingsSetOverrideMutation>(setOverrideMutation);

  const handleFetchMethods = useCallback(() => {
    if (!url) return;
    setFetchError(null);
    commitFetch({
      variables: { input: { url } },
      onCompleted: (response) => {
        const result = response.fetchMcpOauthMethods;
        if (result?.error) {
          setFetchError(result.error);
          setMethods([]);
        } else {
          setMethods([...(result?.methods ?? [])]);
          setFetchError(null);
        }
      },
      onError: (error) => {
        setFetchError(error.message);
        setMethods([]);
      },
    });
  }, [url, commitFetch]);

  const handleMethodChange = useCallback(
    (method: string) => {
      const previousMethod = selectedMethod;
      onMethodChange(method);
      // For existing servers, persist the override immediately
      if (serverId) {
        commitSetOverride({
          variables: {
            input: {
              id: serverId,
              method: method === "auto" ? null : method,
            },
          },
          onCompleted: (response) => {
            if (response.setMcpOauthMethodOverride?.error) {
              setFetchError(response.setMcpOauthMethodOverride.error);
              onMethodChange(previousMethod);
            }
          },
          onError: () => {
            setFetchError("Failed to save auth method override");
            onMethodChange(previousMethod);
          },
        });
      }
    },
    [onMethodChange, selectedMethod, serverId, commitSetOverride],
  );

  return (
    <div className="border border-border/50 rounded-lg overflow-hidden">
      <button
        type="button"
        className="flex items-center justify-between w-full px-4 py-3 text-sm text-muted-foreground hover:text-foreground hover:bg-accent/50 transition-colors"
        onClick={() => setIsOpen((o) => !o)}
      >
        <span>Advanced OAuth Settings</span>
        <ChevronDown className={`size-4 transition-transform ${isOpen ? "rotate-180" : ""}`} />
      </button>

      {isOpen && (
        <div className="px-4 pb-4 space-y-3 border-t border-border/50">
          <p className="text-xs text-muted-foreground pt-3">
            Override the OAuth token auth method if the default selection doesn&apos;t work with your MCP server. By
            default, &quot;None (PKCE)&quot; is preferred per the MCP spec.
          </p>

          <div className="flex items-end gap-2">
            <div className="space-y-1.5 flex-1 max-w-xs">
              <Label htmlFor="mcp-oauth-method" className="text-xs">
                Token Auth Method
              </Label>
              <Select value={selectedMethod} onValueChange={handleMethodChange} disabled={methods.length === 0 && selectedMethod === "auto"}>
                <SelectTrigger id="mcp-oauth-method" className="h-8 text-xs">
                  <SelectValue placeholder="Fetch methods first" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="auto">Auto (recommended)</SelectItem>
                  {methods.map((m) => (
                    <SelectItem key={m} value={m}>
                      {AUTH_METHOD_LABELS[m] ?? m}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="h-8 text-xs"
              onClick={handleFetchMethods}
              disabled={!url || isFetching}
            >
              {isFetching ? <Loader2 className="size-3 mr-1 animate-spin" /> : <RefreshCw className="size-3 mr-1" />}
              Fetch Methods
            </Button>
          </div>

          {fetchError && <p className="text-xs text-destructive">{fetchError}</p>}
          {methods.length > 0 && !fetchError && (
            <p className="text-xs text-muted-foreground">
              Server supports: {methods.map((m) => AUTH_METHOD_LABELS[m] ?? m).join(", ")}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
